import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Load filter metadata via an RTK mutation and optionally mirror into Redux.
 * Single-fetch on mount (avoids the double/triple-fetch bugs in copy-pasted hooks).
 *
 * @param {{
 *   useGetFiltersMutation: Function,
 *   selectMetadata: (state: any) => any,
 *   setMetadataAction?: Function,
 *   skip?: boolean,
 * }} options
 */
export function useFilterMetadata({
  useGetFiltersMutation,
  selectMetadata,
  setMetadataAction,
  skip = false,
}) {
  const dispatch = useDispatch();
  const metadata = useSelector(selectMetadata);
  const [getFilters, mutationState] = useGetFiltersMutation();

  const refetch = useCallback(async () => {
    try {
      const res = await getFilters().unwrap();
      if (setMetadataAction) {
        dispatch(setMetadataAction(res));
      }
      return res;
    } catch (err) {
      // Company parity: log and swallow (callers may still await)
      console.error("Error fetching filter metadata", err);
      return undefined;
    }
  }, [getFilters, dispatch, setMetadataAction]);

  useEffect(() => {
    if (skip) return;
    refetch();
    // Intentionally once on mount / when skip flips — matches Company
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  return {
    metadata,
    filtersArray: metadata?.filtersArray,
    sortOptions: metadata?.sortOptions,
    refetch,
    isLoading: mutationState?.isLoading,
    isError: mutationState?.isError,
    error: mutationState?.error,
  };
}
